import { KontoNumUtils, Model } from "@binale-tech/shared";

/**
 * SKR03/SKR04 chart-of-accounts category. Local to the frontend because
 * the chart resolution helpers (`fixNum`, `getExtNum`, `mergeCategories`)
 * are form-level concerns - backend works directly on `num` strings.
 *
 * M, AM   = USt scopes (output)
 * V, AV   = VSt scopes (input)
 * SAM/SAV = "Storno"-direction auto-BU variants
 * KU      = no-VAT only
 */

export type CategoryBuScope = "KU" | "AV" | "AM" | "SAV" | "SAM" | "V" | "M";

export class Category implements Omit<Model.IExtNum, "key"> {
    readonly buScope!: CategoryBuScope;
    readonly buAuto!: Model.Bu;
    readonly sv13b?: Model.USt13bOption;
    readonly marked?: boolean;
    readonly FNR?: number;
    static readonly DefaultLen: number = 4;

    protected static readonly autoScopes = new Set(["KU", "AV", "AM", "SAV", "SAM"]);

    constructor(
        public readonly num: string,
        public readonly name: string,
        _key?: number | string,
    ) {}

    static unserialize(v: Record<string, unknown> | Category): Category {
        return Object.assign(new Category("", "", undefined), v);
    }

    fixNum(ext: number): Model.IExtNum {
        return KontoNumUtils.fixNum(this, ext, Category.DefaultLen);
    }

    getExtNum(ext: number): string {
        return KontoNumUtils.getExtNum(this.num, ext, Category.DefaultLen);
    }

    getExtNumPrint(ext: number): string {
        return KontoNumUtils.getExtNumPrint(this.num, ext, Category.DefaultLen);
    }

    equalsTo(c: Model.IExtNum): boolean {
        return KontoNumUtils.equalsTo(this, c);
    }

    getDefaultLen() {
        return Category.DefaultLen;
    }

    isAutoBu() {
        return Category.autoScopes.has(this.buScope);
    }

    getAutoBu(): Model.Bu | null {
        if (this.buScope === "KU") {
            return Model.Bu.KU;
        }
        if (this.isAutoBu()) {
            return this.buAuto;
        }
        return null;
    }

    static copyWithFixedNum(
        cat: Category | undefined,
        ext: number,
        overrides: Partial<Pick<Category, "num" | "name">> = {},
    ): Category | undefined {
        if (!cat) {
            return undefined;
        }
        const decoded = JSON.parse(JSON.stringify(cat));
        const { num, name } = overrides;
        return Category.unserialize({ ...decoded, ...(num ? { num } : {}), ...(name ? { name } : {}) }).fixNum(
            ext,
        ) as Category;
    }

    static mergeCategories(
        defaultCat: Map<number, Category>,
        categoryOverrides: Model.INumName[] = [],
        ext: number,
    ): { all: Category[]; user: Category[]; allMap: Map<string, Category> } {
        const allWithOverrides = new Map<string, Category>();
        defaultCat.forEach((cat) => {
            const currentMaxLenNum = KontoNumUtils.getExtNum(cat.num, ext, Category.DefaultLen);
            const fixed = this.copyWithFixedNum(cat, ext);
            if (fixed) {
                allWithOverrides.set(currentMaxLenNum, fixed);
            }
        });
        const onlyOverrides: Category[] = [];
        categoryOverrides.forEach(({ num, name }) => {
            const shortNum = Number(num.substring(0, 4));
            const maxLenNum = KontoNumUtils.getExtNum(num, ext, Category.DefaultLen);
            if (!defaultCat.has(shortNum)) {
                return;
            }
            const defCat = defaultCat.get(shortNum);
            const category = allWithOverrides.has(maxLenNum)
                ? this.copyWithFixedNum(defCat, ext, { name })
                : this.copyWithFixedNum(defCat, ext, { num, name });
            if (!category) {
                return;
            }
            onlyOverrides.push(category);
            allWithOverrides.set(maxLenNum, category);
        });

        const allCats = Array.from(allWithOverrides.values());
        allCats.sort((a, b) => Number(a.num) - Number(b.num));
        onlyOverrides.sort((a, b) => Number(a.num) - Number(b.num));
        return {
            all: allCats,
            user: onlyOverrides,
            allMap: allWithOverrides,
        };
    }
}

export interface CategoryDB {
    num: string;
    Konto: number;
    Status: number;
    ZF?: number;
    HF?: number;
    BU?: number;
    Bericht?: "Bilanz" | "GuV";
    Abschlussposten?: string;
    /**
     * @deprecated in favor of `name`
     */
    Kontenbeschrifung?: string;
    name?: string;
    sv13b?: number;
}

class CategoryDiv extends Category {
    getExtNum(_ext: number) {
        return "Div.";
    }

    getExtNumPrint(_ext: number) {
        return "Div.";
    }
}

export const CATEGORY_EMPTY = new Category("", "");
export const CATEGORY_DIV = new CategoryDiv("Div.", "Div.");
